import React, { useMemo, useRef } from "react";

import { twJoin } from "tailwind-merge";

import { FloatingWindow } from "src/base-components/FloatingWindow/FloatingWindow";
import { PonEditor, PonEditorHandle } from "src/base-components/PONEditor";
import { safeStringify } from "src/cases/caseView/blocks/cellTypes";
import { jsonToPon, ponToJson } from "src/datasets/DatasetTable/utils";
import { Pill } from "src/design-system/Pill";
import { useOnClickOutside } from "src/hooks/usehooks-ts";
import { copyTextToClipboard } from "src/utils/clipboard";

export const JsonEditor: React.FC<{
  value: unknown;
  columnKey: string;
  onChange: (newValue: unknown) => void;
  onBlur: () => void;
  hasLeftIcon?: boolean;
}> = ({ value, columnKey, onChange, onBlur, hasLeftIcon }) => {
  const compactValue = useMemo(() => safeStringify(value), [value]);
  const triggerRef = useRef<HTMLDivElement>(null);

  const ponValue = useMemo(() => {
    try {
      return jsonToPon(JSON.stringify(value, null, 2));
    } catch {
      return jsonToPon("{}");
    }
  }, [value]);

  const editorRef = useRef<PonEditorHandle>(null);

  /** The editor's current text as JSON, or null when it isn't parseable. */
  const readEditedJson = () => {
    const ponValue = editorRef.current?.getValue();
    if (ponValue === undefined) return null;

    try {
      const jsonStr = ponToJson(ponValue);
      JSON.parse(jsonStr);
      return jsonStr;
    } catch {
      return null;
    }
  };

  const commitAndClose = () => {
    const editedJson = readEditedJson();
    if (editedJson !== null && editedJson !== compactValue) {
      onChange(JSON.parse(editedJson));
    }
    onBlur();
  };

  const handleCopy = () => {
    const editedJson = readEditedJson();
    copyTextToClipboard(
      JSON.stringify(
        editedJson !== null ? JSON.parse(editedJson) : value,
        null,
        2,
      ),
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === "Escape") {
      e.preventDefault();
      commitAndClose();
    }
  };

  const dataLoc = `json-editor-${columnKey}`;

  useOnClickOutside(triggerRef, (e) => {
    if ((e.target as Element).closest?.(`[data-loc="${dataLoc}"]`)) return;
    commitAndClose();
  });

  return (
    <div
      ref={triggerRef}
      className="h-full w-full min-w-0"
      onKeyDown={handleKeyDown}
    >
      <FloatingWindow
        button={
          <div
            className={twJoin(
              "flex h-full w-full items-center py-1.5 pr-2",
              hasLeftIcon ? "pl-6" : "pl-1.5",
            )}
          >
            <Pill fontType="code-12" size="sm" variant="gray" maxWidth>
              <Pill.Text>{compactValue.slice(0, 75)}</Pill.Text>
            </Pill>
          </div>
        }
        dataLoc={dataLoc}
        draggable={true}
        height="h-80"
        isOpen={true}
        isTrackingReference={true}
        maximizable={true}
        offset={[0, 8]}
        pinnable={false}
        placement="bottom start"
        title={columnKey}
        width="w-120"
        onClose={commitAndClose}
        onCopy={handleCopy}
      >
        {/* No onChange: it is blur-driven and lands after we have already
            committed, so we read the value on close instead. */}
        <PonEditor ref={editorRef} value={ponValue} />
      </FloatingWindow>
    </div>
  );
};
